/**
 * Codes d'erreur typés remontés par le backend auth (cf. handlers
 * controlplane/auth.go + auth_oidc.go). Garde-fou côté UI : on dispatch
 * sur le code, jamais sur le message (i18n / changement de wording).
 *
 * `unknown` couvre tout ce qui n'est pas dans cette liste, y compris les
 * 5xx — le composant Login affiche un message générique fallback.
 */
export type AuthErrorCode =
    | 'invalid_credentials'
    | 'user_disabled'
    | 'tenant_inactive'
    | 'local_auth_disabled'
    | 'missing_credentials'
    | 'invalid_body'
    | 'no_session'
    | 'session_expired'
    | 'idp_error'
    | 'oidc_state_missing'
    | 'oidc_state_mismatch'
    | 'oidc_state_invalid'
    | 'oidc_exchange_failed'
    | 'oidc_invalid_id_token'
    | 'oidc_nonce_mismatch'
    | 'oidc_missing_email'
    | 'invalid_callback'
    | 'internal_error'
    | 'network_error'
    | 'unknown'

/**
 * Erreur auth typée — toutes les erreurs propagées par le port AuthApi
 * sont des AuthError. Le caller peut dispatcher sur `code` pour afficher
 * un message localisé.
 */
export class AuthError extends Error {
    readonly code: AuthErrorCode
    readonly httpStatus: number | null

    constructor(code: AuthErrorCode, message: string, httpStatus: number | null = null) {
        super(message)
        this.code = code
        this.httpStatus = httpStatus
        this.name = 'AuthError'
    }
}

/**
 * isAuthError type-guard — utile en présentation (`catch (e) { if
 * (isAuthError(e)) ... }`) pour ne pas typer en `unknown`.
 */
export function isAuthError(err: unknown): err is AuthError {
    return err instanceof AuthError
}

/**
 * KNOWN_AUTH_CODES expose la liste des codes typés. Utile aux adapters
 * pour décider si un code remonté serveur doit être conservé tel quel ou
 * downgradé en `unknown` (les tests s'appuient dessus, on peut maintenir
 * ce contrat sans dupliquer).
 */
export const KNOWN_AUTH_CODES: readonly AuthErrorCode[] = [
    'invalid_credentials',
    'user_disabled',
    'tenant_inactive',
    'local_auth_disabled',
    'missing_credentials',
    'invalid_body',
    'no_session',
    'session_expired',
    'idp_error',
    'oidc_state_missing',
    'oidc_state_mismatch',
    'oidc_state_invalid',
    'oidc_exchange_failed',
    'oidc_invalid_id_token',
    'oidc_nonce_mismatch',
    'oidc_missing_email',
    'invalid_callback',
    'internal_error',
    'network_error',
    'unknown',
] as const
